// apps/app/src/coachmark/useCoachmarks.ts
// State + localStorage persistence for the coachmark system. Hydrates from
// localStorage on mount; persists on every closeHint. Falls back to in-memory
// only when storage is unavailable (private mode, quota, etc).

import { useCallback, useMemo, useRef, useState } from 'react';

const STORAGE_KEY = 'aod.coachmarks.v1';

interface PersistedShape {
  seen: string[];
}

function safeRead(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PersistedShape;
    return Array.isArray(parsed.seen) ? parsed.seen : [];
  } catch {
    return [];
  }
}

function safeWrite(seen: ReadonlySet<string>): void {
  try {
    const payload: PersistedShape = { seen: Array.from(seen) };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable — in-memory only for this session.
  }
}

export interface CoachmarkController {
  isOpen(id: string): boolean;
  isSeen(id: string): boolean;
  openHint(id: string): void;
  closeHint(id: string): void;
  resetAll(): void;
}

export function useCoachmarks(): CoachmarkController {
  const [seen, setSeen] = useState<Set<string>>(() => new Set(safeRead()));
  const [openId, setOpenId] = useState<string | null>(null);

  // Mirror openId into a ref so isOpen can read it without a dep on openId.
  // Without this, isOpen's identity changes every time openId flips, which
  // would force the controller memo to recompute and re-trigger any
  // useEffect that depends on the controller — an infinite render loop.
  const openIdRef = useRef(openId);
  openIdRef.current = openId;

  const isOpen = useCallback((id: string) => openIdRef.current === id, []);
  const isSeen = useCallback((id: string) => seen.has(id), [seen]);

  const openHint = useCallback((id: string) => {
    console.log('openHint called with', id);
    setOpenId(id);
  }, []);
  const closeHint = useCallback((id: string) => {
    setOpenId((cur) => (cur === id ? null : cur));
    setSeen((cur) => {
      if (cur.has(id)) return cur;
      const next = new Set(cur);
      next.add(id);
      safeWrite(next);
      return next;
    });
  }, []);
  const resetAll = useCallback(() => {
    setSeen(() => new Set());
    setOpenId(null);
    safeWrite(new Set());
  }, []);

  // Memoize the controller so callers can include `controller` in a useEffect
  // dep array without re-running the effect every render (the callbacks are
  // already stable via useCallback, so this object's identity is stable too).
  return useMemo(
    () => ({ isOpen, isSeen, openHint, closeHint, resetAll }),
    [isOpen, isSeen, openHint, closeHint, resetAll],
  );
}
